import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../api/client';
import endpoints from '../api/endpoints';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  ExternalLink, 
  Calendar,
  Eye,
  Trash2,
  X,
  TrendingUp,
  Database,
  Clock
} from 'lucide-react';
import { CONTENT_TYPES, getBadgeClass } from '../constants/contentTypes';
import ReactMarkdown from 'react-markdown';
import '../assets/css/documents.css';
// Styles centralized in assets/css/documents.css

const DocumentManager = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const [sourceTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const { data: documents, isLoading, error } = useQuery(
    ['documents', searchQuery, contentTypeFilter, sourceTypeFilter, currentPage],
    () => {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (contentTypeFilter) params.append('contentType', contentTypeFilter);
      if (sourceTypeFilter) params.append('sourceType', sourceTypeFilter);
      
      return api.get(`${endpoints.documents.list}?${params}`).then(res => res.data);
    },
    { keepPreviousData: true }
  );

  const { data: stats } = useQuery(
    'documentStats',
    () => api.get(endpoints.documents.stats).then(res => res.data)
  );

  const deleteMutation = useMutation(
    (documentId) => api.delete(endpoints.documents.remove(documentId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
        queryClient.invalidateQueries('documentStats');
      },
    }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
  };

  const handleDeleteDocument = async (documentId, documentTitle) => {
    if (window.confirm(`Are you sure you want to delete "${documentTitle}"? This action cannot be undone.`)) {
      try {
        await deleteMutation.mutateAsync(documentId);
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="document-manager-container">
        <div className="doc-header">
          <h1 className="doc-title">Access Denied</h1>
        </div>
        <div className="doc-access-denied">
          <p>You need administrator privileges to access the Documents section.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="doc-loading-spinner">
        <div>Loading documents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doc-empty-state">
        <div>Error loading documents. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="document-manager-container">
      <div className="doc-header">
        <h1 className="doc-title">Document Manager</h1>
        <div className="doc-search-container">
          <form onSubmit={handleSearch} className="doc-form-inline">
            <input
              className="doc-search-input"
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select
              className="doc-filter-select"
              value={contentTypeFilter}
              onChange={(e) => setContentTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              {Object.entries(CONTENT_TYPES)
                .filter(([_, config]) => !config.future)
                .flatMap(([type, config]) => [
                  <optgroup key={type} label={config.label}>
                    {config.subtypes.map(subtype => (
                      <option key={subtype} value={subtype}>{subtype}</option>
                    ))}
                  </optgroup>
                ])}
            </select>
          </form>
        </div>
      </div>

      {stats && (
        <div className="doc-stats-grid">
          <div className="doc-stat-card">
            <div className="doc-stat-icon">
              <Database size={24} />
            </div>
            <div className="doc-stat-content">
              <div className="doc-stat-value">{stats.totalDocuments || 0}</div>
              <div className="doc-stat-label">Total Documents</div>
            </div>
          </div>
          <div className="doc-stat-card">
            <div className="doc-stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="doc-stat-content">
              <div className="doc-stat-value">{stats.recentUploads || 0}</div>
              <div className="doc-stat-label">Uploaded This Week</div>
            </div>
          </div>
          <div className="doc-stat-card">
            <div className="doc-stat-icon">
              <Clock size={24} />
            </div>
            <div className="doc-stat-content">
              <div className="doc-stat-value">{stats.lastUpdated ? formatDate(stats.lastUpdated) : 'N/A'}</div>
              <div className="doc-stat-label">Last Updated</div>
            </div>
          </div>
        </div>
      )}

      {documents?.documents?.length === 0 ? (
        <div className="doc-empty-state">
          <FileText size={48} className="doc-empty-icon" />
          <div>No documents found</div>
          <div className="doc-empty-subtitle">
            Try adjusting your search criteria or upload new documents
          </div>
        </div>
      ) : (
        <>
          <div className="doc-documents-grid">
            {documents?.documents?.map((document) => (
              <div className="doc-document-card" key={document.id}>
                <div className="doc-document-header">
                  <h3 className="doc-document-title">{document.title}</h3>
                  <div className="doc-document-actions">
                    <button className="doc-action-button" title="View Details" onClick={() => handleViewDocument(document)}>
                      <Eye size={16} />
                    </button>
                    <button className="doc-action-button" title="Open Source" onClick={() => document.url && window.open(document.url, '_blank', 'noopener,noreferrer')}>
                      <ExternalLink size={16} />
                    </button>
                    <button className="doc-action-button doc-action-button--danger" title="Delete" onClick={() => handleDeleteDocument(document.id, document.title)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <p className="doc-document-content">{document.content}</p>

                <div className="doc-document-meta">
                  <div className="doc-meta-item">
                    <span className={getBadgeClass(document.contentType)}>{document.contentType}</span>
                  </div>
                  <div className="doc-meta-item">
                    <Calendar size={14} />
                    {formatDate(document.uploadDate)}
                  </div>
                  <div className="doc-meta-item">
                    <FileText size={14} />
                    {document.sourceType}
                  </div>
                  {document.source && (
                    <div className="doc-meta-item">
                      <ExternalLink size={14} />
                      {document.source}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {documents?.totalPages > 1 && (
            <div className="doc-pagination">
              <button
                className="doc-page-button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {Array.from({ length: Math.min(5, documents.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`doc-page-button ${currentPage === page ? 'doc-page-button--active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                className="doc-page-button"
                disabled={currentPage === documents.totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedDocument && (
        <div className="doc-modal-overlay" onClick={() => setSelectedDocument(null)}>
          <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="doc-modal-header">
              <h2 className="doc-modal-title">{selectedDocument.title}</h2>
              <button className="doc-modal-close" onClick={() => setSelectedDocument(null)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="doc-modal-body">
              <div className="doc-modal-meta">
                <div className="doc-modal-meta-item">
                  <strong>Content Type:</strong>
                  <span className={getBadgeClass(selectedDocument.contentType)}>
                    {selectedDocument.contentType}
                  </span>
                </div>
                <div className="doc-modal-meta-item">
                  <strong>Source Type:</strong>
                  <span>{selectedDocument.sourceType}</span>
                </div>
                <div className="doc-modal-meta-item">
                  <strong>Upload Date:</strong>
                  <span>{formatDate(selectedDocument.uploadDate)}</span>
                </div>
                {selectedDocument.source && (
                  <div className="doc-modal-meta-item">
                    <strong>Source:</strong>
                    <a href={selectedDocument.url} target="_blank" rel="noopener noreferrer" className="doc-modal-link">
                      {selectedDocument.source}
                      <ExternalLink size={14} />
                    </a>
                  </div>
                )}
              </div>
              
              <div className="doc-modal-content">
                <h3>Content</h3>
                <div className="doc-modal-text">
                  <ReactMarkdown>{selectedDocument.fullContent || selectedDocument.content}</ReactMarkdown>
                </div>
              </div>
              
              {selectedDocument.embedding && selectedDocument.embedding.length && (
                <div className="doc-modal-embedding">
                  <h3>Embedding Info</h3>
                  <p>Vector dimension: {selectedDocument.embedding.length}</p>
                </div>
              )}
            </div>
            
            <div className="doc-modal-footer">
              <button className="doc-modal-button" onClick={() => selectedDocument.url && window.open(selectedDocument.url, '_blank', 'noopener,noreferrer')} disabled={!selectedDocument.url}>
                <ExternalLink size={16} />
                Open Source
              </button>
              <button 
                className="doc-modal-button doc-modal-button--danger" 
                onClick={() => {
                  setSelectedDocument(null);
                  handleDeleteDocument(selectedDocument.id, selectedDocument.title);
                }}
              >
                <Trash2 size={16} />
                Delete Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;
